import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

export interface NotionInsight {
  id: string;
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  readTime: string;
  publishedAt: string;
}

export interface NotionBlock {
  type: string;
  id: string;
  [key: string]: any;
}

export interface NotionInsightWithContent extends NotionInsight {
  content: NotionBlock[];
}

async function fetchInsights(lang: string): Promise<NotionInsight[]> {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notion-insights?lang=${lang}`,
    {
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        'Content-Type': 'application/json',
      },
    }
  );
  
  if (!response.ok) {
    console.error('Error fetching insights:', response.statusText);
    throw new Error('Failed to fetch insights');
  }
  
  const data = await response.json();
  return data.insights || [];
}

async function fetchInsight(slug: string, lang: string): Promise<NotionInsightWithContent> {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notion-insights?slug=${encodeURIComponent(slug)}&content=true&lang=${lang}`,
    {
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        'Content-Type': 'application/json',
      },
    }
  );
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Post not found');
    }
    throw new Error('Failed to fetch insight');
  }
  
  return response.json();
}

export function useNotionInsights() {
  const { locale } = useLanguage();
  
  return useQuery({
    queryKey: ['notion-insights', locale],
    queryFn: () => fetchInsights(locale),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useNotionInsight(slug: string) {
  const { locale } = useLanguage();
  
  return useQuery({
    queryKey: ['notion-insight', slug, locale],
    queryFn: () => fetchInsight(slug, locale),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!slug,
  });
}
